'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import dynamic from 'next/dynamic'
import type { ComponentType, MouseEvent, ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { Button, ButtonLink } from '@/components/ui/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/components/ui/dialog'
import { Document } from '@/components/ui/document'
import type { LegalPageKey } from '@/content/legal'
import type { Locale } from '@/i18n/routing'
import { legalDialogKeys } from './legal-keys'

type LegalDialogContextValue = {
  openLegal: (key: LegalPageKey) => void
  close: () => void
}

type LegalDialogProviderProps = {
  locale: Locale
  labels: Record<string, string>
  actions: {
    openInNewTab: string
    close: string
  }
  links: Record<LegalPageKey, string>
  children: ReactNode
}

const LegalDialogContext = createContext<LegalDialogContextValue | null>(null)

type LegalContentComponent = ComponentType<Record<string, never>>
type LegalContentModule = { default: ComponentType }
const LegalDialogLoader = () => (
  <div className="flex min-h-40 items-center justify-center" data-sentry-component="LegalDialogLoader" data-sentry-source-file="legal-dialog-provider.tsx">
    <div
      className="size-7 animate-spin rounded-full border-2 border-mist-300 border-t-mist-950 dark:border-mist-700 dark:border-t-white"
      aria-hidden="true"
    />
    <span className="sr-only">Loading legal document</span>
  </div>
)

const loadLegalContent = (importer: () => Promise<LegalContentModule>) =>
  dynamic<Record<string, never>>(() => importer().then((module) => module.default as LegalContentComponent), {
    ssr: false,
    loading: LegalDialogLoader,
  })

const normalizePathname = (href: string) => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const url = new URL(href, window.location.origin)
    return url.pathname.replace(/\/$/, '') || '/'
  } catch {
    return null
  }
}

const legalContentByLocale: Record<Locale, Record<LegalPageKey, LegalContentComponent>> = {
  nl: {
    'legal-terms': loadLegalContent(() => import('@/content/legal/nl/legal-terms.mdx')),
    'legal-privacy': loadLegalContent(() => import('@/content/legal/nl/legal-privacy.mdx')),
    'legal-cookie': loadLegalContent(() => import('@/content/legal/nl/legal-cookie.mdx')),
    'legal-security': loadLegalContent(() => import('@/content/legal/nl/legal-security.mdx')),
    'legal-api-terms': loadLegalContent(() => import('@/content/legal/nl/legal-api-terms.mdx')),
    'legal-dpa': loadLegalContent(() => import('@/content/legal/nl/legal-dpa.mdx')),
    'legal-support-service': loadLegalContent(() => import('@/content/legal/nl/legal-support-service.mdx')),
    'legal-responsible-disclosure': loadLegalContent(
      () => import('@/content/legal/nl/legal-responsible-disclosure.mdx'),
    ),
    'legal-subprocessors': loadLegalContent(() => import('@/content/legal/nl/legal-subprocessors.mdx')),
    'legal-acceptable-use': loadLegalContent(() => import('@/content/legal/nl/legal-acceptable-use.mdx')),
  },
  en: {
    'legal-terms': loadLegalContent(() => import('@/content/legal/en/legal-terms.mdx')),
    'legal-privacy': loadLegalContent(() => import('@/content/legal/en/legal-privacy.mdx')),
    'legal-cookie': loadLegalContent(() => import('@/content/legal/en/legal-cookie.mdx')),
    'legal-security': loadLegalContent(() => import('@/content/legal/en/legal-security.mdx')),
    'legal-api-terms': loadLegalContent(() => import('@/content/legal/en/legal-api-terms.mdx')),
    'legal-dpa': loadLegalContent(() => import('@/content/legal/en/legal-dpa.mdx')),
    'legal-support-service': loadLegalContent(() => import('@/content/legal/en/legal-support-service.mdx')),
    'legal-responsible-disclosure': loadLegalContent(
      () => import('@/content/legal/en/legal-responsible-disclosure.mdx'),
    ),
    'legal-subprocessors': loadLegalContent(() => import('@/content/legal/en/legal-subprocessors.mdx')),
    'legal-acceptable-use': loadLegalContent(() => import('@/content/legal/en/legal-acceptable-use.mdx')),
  },
}

export function LegalDialogProvider({ locale, labels, actions, links, children }: LegalDialogProviderProps) {
  const [activeKey, setActiveKey] = useState<LegalPageKey | null>(null)
  const openLegal = useCallback((key: LegalPageKey) => setActiveKey(key), [])
  const close = useCallback(() => setActiveKey(null), [])

  const legalPathToKey = useMemo(() => {
    const map: Record<string, LegalPageKey> = {}
    for (const key of legalDialogKeys) {
      const href = links[key]
      if (typeof href !== 'string') {
        continue
      }
      const normalized = href.replace(/\/$/, '') || '/'
      map[normalized] = key
    }
    return map
  }, [links])

  const handleDialogLinkClickCapture = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!activeKey || event.defaultPrevented || event.button !== 0) {
        return
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      const target = event.target
      if (!(target instanceof Element)) {
        return
      }

      const anchor = target.closest('a')
      if (!anchor) {
        return
      }

      if ((anchor.getAttribute('target') ?? '').toLowerCase() === '_blank' || anchor.hasAttribute('download')) {
        return
      }

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) {
        return
      }

      const pathname = normalizePathname(href)
      if (!pathname) {
        return
      }

      const nextKey = legalPathToKey[pathname]
      if (!nextKey) {
        return
      }

      event.preventDefault()
      setActiveKey(nextKey)
    },
    [activeKey, legalPathToKey],
  )

  const Content = activeKey ? legalContentByLocale[locale][activeKey] : null
  const title = activeKey ? labels[activeKey] : ''
  const href = activeKey ? links[activeKey] : '#'

  const value = useMemo(() => ({ openLegal, close }), [openLegal, close])

  return (
    <LegalDialogContext.Provider value={value} data-sentry-element="LegalDialogContext.Provider" data-sentry-component="LegalDialogProvider" data-sentry-source-file="legal-dialog-provider.tsx">
      {children}
      <Dialog open={Boolean(activeKey)} onClose={close} size="4xl" data-sentry-element="Dialog" data-sentry-source-file="legal-dialog-provider.tsx">
        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="legal-dialog-provider.tsx">{title}</DialogTitle>
        <DialogBody onClickCapture={handleDialogLinkClickCapture} data-sentry-element="DialogBody" data-sentry-source-file="legal-dialog-provider.tsx">
          {/* `Document` leaves heading sizes to its consumer — see the note there. */}
          {Content ? (
            <Document className="[&_h2]:text-base/8">
              <Content />
            </Document>
          ) : null}
        </DialogBody>
        <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="legal-dialog-provider.tsx">
          {activeKey ? (
            <ButtonLink href={href} target="_blank" rel="noreferrer noopener" size="lg" color="light">
              {actions.openInNewTab}
            </ButtonLink>
          ) : null}
          <Button type="button" size="lg" color="dark/light" onClick={close} data-sentry-element="Button" data-sentry-source-file="legal-dialog-provider.tsx">
            {actions.close}
          </Button>
        </DialogActions>
      </Dialog>
    </LegalDialogContext.Provider>
  )
}

export function useLegalDialog() {
  const context = useContext(LegalDialogContext)
  if (!context) {
    throw new Error('useLegalDialog must be used within LegalDialogProvider')
  }
  return context
}
